import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@/theme/useTheme";

const ASSISTANT_SCRIPT_ID = "sigmobi-assistant-widget-script";
const ASSISTANT_SCRIPT_SRC = "https://cdn.sigmobi.com/assistant-widget/widget.iife.js";
const ASSISTANT_GATEWAY_URL = "https://proxy.sigmobi.com";
const ASSISTANT_REQUEST_TIMEOUT_MS = 30000;

interface SigMobiChatConfig {
  gatewayUrl: string;
  jwtToken?: string;
  theme: "light" | "dark";
  title: string;
  requestTimeoutMs: number;
}

interface SigMobiChatApi {
  init: (config: SigMobiChatConfig) => void;
  destroy: () => void;
}

type ScriptStatus = "idle" | "loaded" | "error";

declare global {
  interface Window {
    SigMobiChat?: SigMobiChatApi;
  }
}

const getScriptStatus = (script: HTMLScriptElement | null): ScriptStatus => {
  const status = script?.dataset.status;

  if (status === "loaded" || status === "error") {
    return status;
  }

  return "idle";
};

const validateAssistantApi = () => {
  if (!window.SigMobiChat) {
    throw new Error("Assistant widget API is unavailable after script load");
  }
};

const loadAssistantScript = () =>
  new Promise<void>((resolve, reject) => {
    if (typeof window === "undefined") {
      resolve();
      return;
    }

    if (window.SigMobiChat) {
      resolve();
      return;
    }

    const existingScript = document.getElementById(ASSISTANT_SCRIPT_ID) as HTMLScriptElement | null;

    if (existingScript) {
      const status = getScriptStatus(existingScript);

      if (status === "loaded") {
        try {
          validateAssistantApi();
          resolve();
        } catch (error) {
          reject(error);
        }
        return;
      }

      if (status === "error") {
        reject(new Error("Failed to load assistant widget script"));
        return;
      }

      existingScript.addEventListener(
        "load",
        () => {
          try {
            validateAssistantApi();
            resolve();
          } catch (error) {
            reject(error);
          }
        },
        { once: true },
      );
      existingScript.addEventListener("error", () => reject(new Error("Failed to load assistant widget script")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = ASSISTANT_SCRIPT_ID;
    script.src = ASSISTANT_SCRIPT_SRC;
    script.async = true;
    script.dataset.status = "idle";
    script.onload = () => {
      script.dataset.status = "loaded";

      try {
        validateAssistantApi();
        resolve();
      } catch (error) {
        reject(error);
      }
    };
    script.onerror = () => {
      script.dataset.status = "error";
      reject(new Error("Failed to load assistant widget script"));
    };
    document.body.appendChild(script);
  });

const AssistantWidget = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();

  useEffect(() => {
    let isMounted = true;

    const initializeAssistant = async () => {
      try {
        await loadAssistantScript();

        if (!isMounted || !window.SigMobiChat) {
          return;
        }

        window.SigMobiChat.init({
          gatewayUrl: ASSISTANT_GATEWAY_URL,
          theme,
          title: t("assistant.title"),
          requestTimeoutMs: ASSISTANT_REQUEST_TIMEOUT_MS,
        });
      } catch (error) {
        console.error("[AssistantWidget] Failed to initialize assistant widget.", error);
      }
    };

    initializeAssistant();

    return () => {
      isMounted = false;
      window.SigMobiChat?.destroy();
    };
  }, [theme, t]);

  return null;
};

export default AssistantWidget;
